import { Injectable } from '@angular/core';
import { FormArray, UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { sipPasswordValidators } from 'src/app/common-components/validators/password.validator';
import { DeviceItem, PhoneRegisterListItem } from '../../phones/models/phone-register-results.model';
import { PhoneDetailView } from '../models/phone-detail-view.model';

@Injectable()
export class PhoneDetailFormService extends FormsBase {
   private sipUserFormArray: UntypedFormArray;

   private hardphoneFormArray: UntypedFormArray;

   private handsetFormArray: UntypedFormArray;

   constructor(private fb: UntypedFormBuilder) {
      super();

      this.sipUserFormArray = fb.array([]);

      this.hardphoneFormArray = fb.array([]);

      this.handsetFormArray = fb.array([]);

      this.form = fb.group({
         hardphoneArray: this.hardphoneFormArray,
         hardphoneSelect: fb.control(null),
         phoneType: fb.control(null),
         sipUserArray: this.sipUserFormArray,
         handsetArray: this.handsetFormArray,
      });
   }

   addSipUser() {
      this.sipUserFormArray.push(
         this.fb.group({
            sipPassword: this.fb.control(null, sipPasswordValidators()),
            sipUserId: null,
            sipUsername: this.fb.control(null, [containsCharactersValidator(), Validators.required]),
         })
      );
   }

   addHardphoneToExtension() {
      this.hardphoneFormArray.push(
         this.fb.group({
            device: this.fb.control(null),
            numberOfLineKeys: this.fb.control(null, [
               Validators.required,
               Validators.pattern(/^\d+$/),
               Validators.min(1),
               Validators.max(30),
            ]),
         })
      );
   }

   addHandset() {
      this.handsetFormArray.push(
         this.fb.group({
            handsetIpei: this.fb.control(null, [Validators.pattern(/^[a-zA-Z0-9:]+$/), Validators.maxLength(10)]),
            baseStationHandsets: this.fb.array([], Validators.required),
         })
      );
   }

   updatePhone(phone: PhoneDetailView): void {
      if (!phone) {
         return;
      }

      const baseStationHandsetsArray = this.handsetFormArray.controls[0]?.get('baseStationHandsets') as FormArray;

      if (baseStationHandsetsArray) {
         phone.baseStationHandsets.forEach((baseStationHandset) => {
            baseStationHandsetsArray.push(
               this.fb.group({
                  baseStationHandsetId: baseStationHandset.baseStationHandsetId,
                  deviceId: baseStationHandset.deviceId,
                  sortId: baseStationHandset.sortId,
                  phoneId: phone.phoneId,
                  device: {
                     macAddress: baseStationHandset.device?.macAddress,
                     description: baseStationHandset.device?.description,
                     deviceTypeId: baseStationHandset.device?.deviceTypeId,
                  },
               })
            );
         });
      }

      this.form.patchValue({
         phoneType: phone.phoneTypeId,
         sipUserArray: phone.sipUsers.map((sipUser) => {
            return {
               sipPassword: sipUser.hashPassword,
               sipUserId: sipUser.sipUserId,
               sipUsername: sipUser.userName,
            };
         }),
         hardphoneArray: phone.devices.map((device) => {
            return {
               device: device,
               numberOfLineKeys: phone.numberOfLineKeys,
            };
         }),
         handsetArray: [
            {
               handsetIpei: phone.handsetIpei,
            },
         ],
      });
      this.markAsUntouchedAndPristine();
   }

   getHandsetValue(phoneId: number | null, extensionId: number | null): PhoneRegisterListItem {
      const formValue = this.form.value;

      const baseStations = formValue.handsetArray.flatMap((handset) => {
         return handset.baseStationHandsets.map((baseStation) => {
            return {
               baseStationHandsetId: baseStation.baseStationHandsetId ?? 0,
               deviceId: baseStation.deviceId == 0 ? baseStation.value.deviceId : baseStation.deviceId,
               device: {
                  macAddress: baseStation?.value?.macAddress ?? baseStation.device.macAddress,
                  deviceTypeId: baseStation?.value?.deviceTypeId ?? baseStation.device.deviceTypeId,
               },
               phoneId: phoneId,
               sortId: baseStation.sortId ?? 1, // sortId will be modified if needed in the API
            };
         });
      });

      return {
         phoneId: phoneId,
         phoneType: formValue.phoneType,
         numberOfLineKeys: null,
         handsetIpei: formValue.handsetArray[0].handsetIpei,
         baseStationHandsets: baseStations,
         createDate: null,
         updateDate: null,
         createUserName: null,
         updateUserName: null,
         name: formValue.handsetArray[0].handsetIpei,
         sipUsers: [],
      };
   }

   getValue(phoneId: number | null, extensionId: number | null): PhoneRegisterListItem {
      const formValue = this.form.value;

      return {
         phoneId: phoneId,
         phoneType: formValue.phoneType,
         numberOfLineKeys: null,
         handsetIpei: formValue.handsetIpei,
         baseStationHandsets: formValue.baseStationHandsets,
         sipUsers: formValue.sipUserArray
            ? formValue.sipUserArray.map((sipUser) => {
                 return {
                    hashPassword: sipUser.sipPassword,
                    phoneId: phoneId,
                    sipUserId: sipUser.sipUserId,
                    userName: sipUser.sipUsername,
                 };
              })
            : [],
         createDate: null,
         updateDate: null,
         createUserName: null,
         updateUserName: null,
         name:
            formValue.sipUserArray && formValue.sipUserArray.length > 0 ? formValue.sipUserArray[0].sipUsername : null,
      };
   }

   getHardphoneValue(phoneId: number | null, extensionId: number | null): PhoneRegisterListItem {
      const formValue = this.form.value;

      const hardphone = formValue.hardphoneArray[0];

      const devices: DeviceItem[] = formValue.hardphoneArray.map((hardphone) => {
         return hardphone.device;
      });

      return {
         phoneId: phoneId,
         phoneType: formValue.phoneType,
         numberOfLineKeys: hardphone.numberOfLineKeys,
         devices: devices,
         baseStationHandsets: [],
         createDate: null,
         updateDate: null,
         createUserName: null,
         updateUserName: null,
         name: `${hardphone.device.deviceTypeId} - ${hardphone.device.macAddress}`,
         sipUsers: [],
      };
   }
}
